import { createContext, useContext, useEffect, useState } from 'react';
import { getChildrenLayout, VIEW_MODES } from './content-ops';
import { useEvent } from '../../shared-components/useEvent';

export const safelySpread = (object) => ({ ...(object || {}) });

export function isObject(o) {
  return o && typeof o === 'object' && !Array.isArray(o);
}

export function mergeDeep(target, source) {
  const output = { ...target };
  for (const key of Object.keys(source)) {
    const src = source[key],
      tgt = target[key];
    if (isObject(src) && isObject(tgt)) {
      output[key] = mergeDeep(tgt, src);
    } else {
      output[key] = src;
    }
  }
  return output;
}

const ContentProviderContext = createContext();

export const useContent = () => {
  const context = useContext(ContentProviderContext);
  if (!context) {
    return {};
  }
  return context;
};

const getDigitalViewMode = (viewMode) =>
  viewMode === VIEW_MODES.DESKTOP || viewMode === VIEW_MODES.MOBILE
    ? window.innerWidth < 600
      ? VIEW_MODES.MOBILE
      : VIEW_MODES.DESKTOP
    : viewMode;

export const Content = ({ resources, page, themeLoaded, children, type, globalSettings, updateContentPosition }) => {
  const [viewMode, setViewMode] = useState(getDigitalViewMode(type));
  const [layout, setLayout] = useState({});

  useEffect(() => {
    if (!page?.Content) {
      setLayout({});
      return;
    }
    const layout = getChildrenLayout(page.Content, viewMode, globalSettings);
    setLayout(layout);
  }, [viewMode, page?.Content, globalSettings]);

  useEvent('resize', () => {
    setViewMode(getDigitalViewMode(viewMode));
  });

  return (
    <ContentProviderContext.Provider
      value={{
        resources,
        page,
        content: page?.Content || {},
        layout,
        themeLoaded,
        viewMode,
        globalSettings,
        updateContentPosition,
      }}
    >
      {children}
    </ContentProviderContext.Provider>
  );
};

// Referentially safe constants:
Content.CONSTANTS = {
  EMPTY_OBJECT: Object.freeze({}),
  EMPTY_ARRAY: Object.freeze([]),
};

Content.getGlobalSettings = (viewer) => viewer?.globalSettings || Content.CONSTANTS.EMPTY_OBJECT;
Content.getViewMode = (viewer) => viewer?.viewMode;
Content.getPage = (viewer) => viewer?.page;
Content.get = (viewer, id) => {
  if (id in (viewer?.content?.data || {})) {
    return viewer?.content?.data[id];
  } else {
    const section = viewer?.content?.sections?.find((section) => section.id === id);
    if (section) {
      return section;
    }
  }
  return {};
};

Content.getParentBlock = (viewer, id) => {
  const block = Content.get(viewer, id);
  return Content.get(viewer, block?.parentId);
};
Content.getRoot = (viewer) => viewer?.content?.root || {};
Content.getContent = (viewer) => viewer?.content || {};
Content.getResource = (viewer, resource) => viewer?.resources?.[resource] || {};

Content.getMedia = (viewer, id) => {
  const block = Content.get(viewer, id);
  if (block?.type === 'section') {
    return block?.properties || Content.CONSTANTS.EMPTY_OBJECT;
  } else {
    return block?.properties?.media || Content.CONSTANTS.EMPTY_OBJECT;
  }
};

Content.traverseDescendants = (viewer, id, fn) => {
  const block = Content.get(viewer, id);
  fn(block);
  if (block?.children) {
    for (const child of block.children) {
      Content.traverseDescendants(viewer, child.id, fn);
    }
  }
};

Content.getChildrenById = (viewer, id, options = {}) => {
  const { layout } = viewer;
  const { useOriginal = false } = options;

  const block = Content.get(viewer, id);
  const originalChildren = block?.children ?? Content.CONSTANTS.EMPTY_ARRAY;
  const layoutChildren = layout[id] ?? originalChildren;
  const children = useOriginal ? originalChildren : layoutChildren;
  return children;
};

Content.getPropertiesById = (viewer, id, viewMode = VIEW_MODES.DESKTOP, options = {}) => {
  const block = Content.get(viewer, id);
  if (!viewMode || viewMode === VIEW_MODES.DESKTOP) {
    return block?.properties || Content.CONSTANTS.EMPTY_OBJECT;
  }
  const { views = {}, ...properties } = block?.properties || {};
  const viewDelta = { ...(views?.[viewMode] || {}) };
  return mergeDeep(properties, viewDelta);
};

Content.getColumnWidth = (viewer, { id, parentId, viewMode }) => {
  const parent = Content.get(viewer, parentId);
  const siblings = Content.getChildrenById(viewer, parentId);
  const properties = Content.getPropertiesById(viewer, id, viewMode, { honorViewMode: false });
  const media = Content.getMedia(viewer, id)?.[viewMode];

  let width;
  if (viewMode === VIEW_MODES.DESKTOP) {
    width = properties?.width;
  } else if (parent?.children?.length && siblings?.length !== parent?.children?.length) {
    width = media?.width;
  } else {
    width = media?.width || properties?.width;
  }

  return (width || 1) * 100;
};
